import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, User, Mail, Phone, Crown, Edit2, Save, 
  Award, TrendingUp, Wallet, Share2, Copy, X, 
  ChevronRight, ShieldCheck, Fingerprint 
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import logo from "../assets/logo64.png";

const Profile: React.FC = () => {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(profile?.name || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [loading, setLoading] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  useEffect(() => {
    if (showWalletModal) {
      loadReferrals();
    }
  }, [showWalletModal]);

  const loadReferrals = async () => {
    setLoadingReferrals(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('name, email, created_at, admin')
        .eq('referred_by', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReferrals(data || []);
    } catch (error: any) {
      console.error('Error loading referrals:', error);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ name, phone })
        .eq('id', user?.id);

      if (error) throw error;
      toast.success('Profile updated successfully!');
      setEditing(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferralLink = () => {
    const referralLink = `${window.location.origin}/signup?ref=${user?.id}`;
    navigator.clipboard.writeText(referralLink);
    toast.success('Link copied to terminal clipboard');
  };

  const getPlanName = (plan_id: string): string => {
    const types: Record<string, string> = {
      "055e58d2-2df5-4ea7-8f82-fd92c56fc49c": "Platinum",
      "2326cde6-c9b4-4b68-ba66-84bcde479d66": "Gold",
      "9441816c-0170-4cb4-9d8a-57f3618ab723": "Silver",
    };
    return types[plan_id] || "Standard";
  };

  return (
    <div className="min-h-screen bg-[#08090a] text-white antialiased font-sans selection:bg-orange-500/30">
      {/* Background Accents */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
             <button onClick={() => navigate('/home')} className="p-2 hover:bg-white/5 rounded-xl transition-colors text-gray-400 hover:text-white">
                <ArrowLeft size={20} />
             </button>
             <h1 className="text-sm font-black tracking-[0.2em] uppercase text-gray-400">User Terminal</h1>
          </div>
          <Link to="/" className="flex items-center gap-3 group no-underline">
            <span className="text-xl font-black tracking-tighter italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
              ytta
            </span>
          </Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        
        {/* Profile Header Card */}
        <div className="relative group mb-6">
          <div className="absolute -inset-1 bg-gradient-to-r from-orange-500/20 to-transparent rounded-[2.5rem] blur opacity-25" />
          <div className="relative bg-white/[0.02] backdrop-blur-xl rounded-[2.5rem] p-8 border border-white/5 flex flex-col items-center text-center">
             <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-orange-500 to-orange-600 p-[1px] mb-6 shadow-xl shadow-orange-500/20">
                <div className="w-full h-full bg-[#08090a] rounded-[23px] flex items-center justify-center text-3xl font-black italic text-white">
                    {profile?.name?.charAt(0) || 'V'}
                </div>
             </div>
             <h2 className="text-3xl font-black tracking-tighter italic mb-1">{profile?.name}</h2>
             <p className="text-gray-500 text-xs font-bold tracking-widest uppercase mb-6">{profile?.email}</p>
             
             <div className="grid grid-cols-3 gap-8 w-full pt-6 border-t border-white/5">
                <div>
                    <div className="text-lg font-black text-white italic">127</div>
                    <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Calls</div>
                </div>
                <div>
                    <div className="text-lg font-black text-orange-500 italic">89%</div>
                    <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Win Rate</div>
                </div>
                <div>
                    <div className="text-lg font-black text-white italic">45</div>
                    <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Mtd</div>
                </div>
             </div>
          </div>
        </div>

        {/* Action Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Wallet Card */}
            <button 
                onClick={() => setShowWalletModal(true)}
                className="group relative bg-white/[0.02] hover:bg-white/[0.04] backdrop-blur-xl rounded-[2rem] p-6 border border-white/5 transition-all text-left"
            >
                <div className="flex justify-between items-start mb-4">
                    <div className="p-3 rounded-2xl bg-orange-500/10 text-orange-500">
                        <Wallet size={20} />
                    </div>
                    <ChevronRight size={18} className="text-gray-600 group-hover:text-orange-500 transition-colors" />
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Available Credits</div>
                <div className="text-2xl font-black italic text-white">₹{profile?.walletvalue || 0}</div>
            </button>

            {/* Plan Card */}
            <div className="relative bg-white/[0.02] backdrop-blur-xl rounded-[2rem] p-6 border border-white/5">
                <div className="flex justify-between items-start mb-4">
                    <div className="p-3 rounded-2xl bg-white/5 text-white">
                        <Crown size={20} />
                    </div>
                    <div className="px-2 py-1 rounded-md bg-orange-500 text-[8px] font-black uppercase tracking-tighter">Active</div>
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1">Access Tier</div>
                <div className="text-2xl font-black italic text-white">{getPlanName(profile?.plan_id)}</div>
            </div>
        </div>

        {/* Settings Form */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-[2.5rem] p-8 border border-white/5">
           <div className="flex items-center justify-between mb-8">
              <h3 className="text-xs font-black uppercase tracking-[0.3em] text-orange-500">Terminal Settings</h3>
              {!editing ? (
                <button onClick={() => setEditing(true)} className="p-2 hover:bg-white/5 rounded-xl text-gray-400 transition-colors">
                    <Edit2 size={18} />
                </button>
              ) : (
                <button onClick={handleSave} disabled={loading} className="px-4 py-2 bg-white text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-orange-500 hover:text-white transition-all">
                    {loading ? 'Syncing...' : 'Save Changes'}
                </button>
              )}
           </div>

           <div className="space-y-6">
              <InputGroup label="Alias" icon={<User size={18}/>} value={name} onChange={setName} disabled={!editing} />
              <InputGroup label="Contact" icon={<Phone size={18}/>} value={phone} onChange={setPhone} disabled={!editing} type="tel" />
              <div className="opacity-50">
                <InputGroup label="Network ID" icon={<Mail size={18}/>} value={profile?.email || ''} onChange={() => {}} disabled={true} />
              </div>
           </div>
           
           {editing && (
             <button onClick={() => setEditing(false)} className="w-full mt-6 text-[10px] font-black text-gray-600 hover:text-white uppercase tracking-widest transition-colors">
                Discard Changes
             </button>
           )}
        </div>
      </main>

      {/* Wallet Modal - Vytta Style */}
      {showWalletModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowWalletModal(false)} />
          <div className="relative w-full max-w-xl bg-[#0b0c0e] rounded-[3rem] border border-white/10 shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
            <div className="p-8 border-b border-white/5 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <Wallet className="text-orange-500" size={24} />
                    <h2 className="text-xl font-black italic tracking-tighter">Vytta Wallet</h2>
                </div>
                <button onClick={() => setShowWalletModal(false)} className="p-2 hover:bg-white/5 rounded-full text-gray-500">
                    <X size={20} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-8">
                {/* Balance Display */}
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-[2rem] p-8 text-white relative overflow-hidden shadow-lg shadow-orange-500/20">
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80 mb-2">Institutional Credit Balance</p>
                        <h3 className="text-5xl font-black italic tracking-tighter mb-8">₹{profile?.walletvalue || 0}</h3>
                        <button 
                            onClick={handleCopyReferralLink}
                            className="w-full py-4 bg-black rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-all flex items-center justify-center gap-2"
                        >
                            <Share2 size={14} /> Invite Network
                        </button>
                    </div>
                    <div className="absolute top-[-20%] right-[-10%] w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                </div>

                {/* Referrals Section */}
                <div>
                    <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 mb-4 px-2">Affiliate Log</h4>
                    {loadingReferrals ? (
                        <div className="animate-pulse space-y-3">
                            {[1,2].map(i => <div key={i} className="h-16 bg-white/5 rounded-2xl" />)}
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {referrals.map((ref, i) => (
                                <div key={i} className="bg-white/[0.03] border border-white/5 rounded-2xl p-4 flex justify-between items-center">
                                    <div>
                                        <div className="text-sm font-bold">{ref.name}</div>
                                        <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest">{new Date(ref.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <div className="text-orange-500 font-black italic">+₹50</div>
                                </div>
                            ))}
                            {referrals.length === 0 && <div className="text-center py-8 text-gray-600 text-xs font-bold uppercase tracking-widest border-2 border-dashed border-white/5 rounded-[2rem]">No active nodes found</div>}
                        </div>
                    )}
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InputGroup = ({ label, icon, value, onChange, disabled, type = "text" }: any) => (
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-600 ml-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-5 top-1/2 -translate-y-1/2 text-orange-500">
          {icon}
        </div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full pl-14 pr-6 py-4 bg-white/[0.03] rounded-2xl border border-white/5 focus:border-orange-500 focus:ring-1 focus:ring-orange-500/50 outline-none transition-all font-bold text-sm disabled:opacity-50"
        />
      </div>
    </div>
);

export default Profile;